import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  CircularProgress,
  InputAdornment,
  Stack,
  useTheme,
  useMediaQuery,
  Button,
  Autocomplete,
  Grid,
  IconButton,
  Chip,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  AccountBalance as AccountBalanceIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PictureAsPdfIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Assessment as AssessmentIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { useNavigate, useSearchParams } from 'react-router-dom';
import * as XLSX from 'xlsx';
import carteraService, { MovimientoCartera, SaldoCartera } from '../../services/carteraService';
import { getMovimientoCajaPdfConfig } from '../../services/tesoreriaCajaService';
import api from '../../services/api';
import { formatCurrency, formatDate } from '../../utils/formatters';
import { getFileNameFromContentDisposition } from '../../utils/contentDisposition';
import PDFViewer from '../../components/shared/PDFViewer';
import { searchableSelectAutocompleteProps } from '../../components/SearchableSelect';
import {
  loadTesoreriaFiltros,
  storedNumber,
  storedString,
  TESORERIA_FILTROS_KEYS,
  usePersistTesoreriaFiltros,
} from '../../utils/tesoreriaFiltrosStorage';
import {
  aplicarSaldoAcumuladoMovimientosCartera,
  deduplicarMovimientosCartera,
  ordenarMovimientosCarteraParaVisualizacion,
} from '../../utils/movimientosCarteraSaldo';
import {
  filtrarMovimientosPorTipoDocumento,
  getComprobanteMovimientoLabel,
  getTipoMovimientoLabel,
  getTiposDocumentoEnListado,
  TIPOS_DOCUMENTO_EXCLUIDOS_DEFAULT,
} from '../../utils/movimientosCarterasLabels';
import ArqueoCarteraDialog from '../../components/Tesoreria/ArqueoCarteraDialog';
import {
  ArqueoCarteraDrillDown,
  filtrarMovimientosDrillDownArqueo,
  formatEtiquetaDrillDownArqueo,
} from '../../utils/arqueoCartera';
import { hasBrowserSession } from '../../utils/sessionAuthStore';

interface MovimientosCarterasFiltrosStored {
  fechaDesde?: string;
  fechaHasta?: string;
  searchTerm?: string;
  rowsPerPage?: number;
  nroBancoCartera?: number;
  tiposExcluidos?: string[];
}

const tiposExcluidosFromStorage = (stored: Partial<MovimientosCarterasFiltrosStored>): string[] => {
  if (Array.isArray(stored.tiposExcluidos)) {
    return stored.tiposExcluidos.filter((t): t is string => typeof t === 'string');
  }
  return [...TIPOS_DOCUMENTO_EXCLUIDOS_DEFAULT];
};

const tiposExcluidosSonDefault = (tiposExcluidos: string[]): boolean => {
  if (tiposExcluidos.length !== TIPOS_DOCUMENTO_EXCLUIDOS_DEFAULT.length) return false;
  const ordenados = [...tiposExcluidos].sort();
  const defaultOrdenados = [...TIPOS_DOCUMENTO_EXCLUIDOS_DEFAULT].sort();
  return ordenados.every((t, i) => t === defaultOrdenados[i]);
};

// Interfaz para representar una fila con saldo acumulado
interface MovimientoRow {
  movimiento: MovimientoCartera;
  saldoAcumulado?: number;
}

// Obtener primer y último día del mes actual en formato YYYY-MM-DD
const getMesActual = () => {
  const now = new Date();
  const primerDia = new Date(now.getFullYear(), now.getMonth(), 1);
  const ultimoDia = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    desde: primerDia.toISOString().slice(0, 10),
    hasta: ultimoDia.toISOString().slice(0, 10),
  };
};

const MovimientosCarteras: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [searchParams] = useSearchParams();
  const loadStored = () =>
    loadTesoreriaFiltros<MovimientosCarterasFiltrosStored>(TESORERIA_FILTROS_KEYS.movimientosCarteras);

  const [carteras, setCarteras] = useState<SaldoCartera[]>([]);
  const [carteraSeleccionada, setCarteraSeleccionada] = useState<SaldoCartera | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingCarteras, setLoadingCarteras] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(() => storedNumber(loadStored(), 'rowsPerPage', 10));
  const MAX_PAGE_SIZE = 20; // Límite máximo del backend por request
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPDF, setExportingPDF] = useState(false);
  const [openArqueoDialog, setOpenArqueoDialog] = useState(false);
  const [drillDownFiltro, setDrillDownFiltro] = useState<ArqueoCarteraDrillDown | null>(null);
  const [todosLosMovimientos, setTodosLosMovimientos] = useState<MovimientoCartera[]>([]);
  const [fechaDesde, setFechaDesde] = useState<string>(() =>
    storedString(loadStored(), 'fechaDesde', getMesActual().desde)
  );
  const [fechaHasta, setFechaHasta] = useState<string>(() =>
    storedString(loadStored(), 'fechaHasta', getMesActual().hasta)
  );
  const [searchTerm, setSearchTerm] = useState(() => storedString(loadStored(), 'searchTerm', ''));
  const [tiposExcluidos, setTiposExcluidos] = useState<string[]>(() =>
    tiposExcluidosFromStorage(loadStored())
  );
  const [consultandoMovimientoId, setConsultandoMovimientoId] = useState<number | null>(null);
  const [pdfMovimientoUrl, setPdfMovimientoUrl] = useState<string | null>(null);
  const [pdfMovimientoId, setPdfMovimientoId] = useState<number | null>(null);
  const [pdfMovimientoTitle, setPdfMovimientoTitle] = useState<string | null>(null);
  const [pdfMovimientoFileName, setPdfMovimientoFileName] = useState<string | null>(null);
  const [pdfMovimientoDocumentType, setPdfMovimientoDocumentType] = useState<
    'recibo' | 'ordenPago' | 'transferenciaCaja' | undefined
  >(undefined);

  usePersistTesoreriaFiltros(
    TESORERIA_FILTROS_KEYS.movimientosCarteras,
    {
      fechaDesde,
      fechaHasta,
      searchTerm,
      rowsPerPage,
      nroBancoCartera: carteraSeleccionada?.nroBanco,
      tiposExcluidos,
    },
    [fechaDesde, fechaHasta, searchTerm, rowsPerPage, carteraSeleccionada?.nroBanco, tiposExcluidos]
  );

  // Cargar todos los movimientos (paginando en el API) y calcular saldo acumulado en cliente
  const cargarMovimientosCartera = async (nroBanco: number) => {
    try {
      setLoading(true);
      const fechaDesdeParam = fechaDesde ? `${fechaDesde}T00:00:00` : undefined;
      const fechaHastaParam = fechaHasta ? `${fechaHasta}T23:59:59` : undefined;
      const term = searchTerm.trim() || undefined;

      const acumulados: MovimientoCartera[] = [];
      let currentPage = 1;
      let saldoInicialApi: number | undefined;
      let hasMore = true;

      while (hasMore) {
        const response = await carteraService.getMovimientos(nroBanco, {
          pageNumber: currentPage,
          pageSize: MAX_PAGE_SIZE,
          fechaDesde: fechaDesdeParam,
          fechaHasta: fechaHastaParam,
          searchTerm: term,
        });

        if (currentPage === 1 && response.saldoInicial !== undefined && response.saldoInicial !== null) {
          saldoInicialApi = response.saldoInicial;
        }

        if (response.items?.length) {
          acumulados.push(...response.items);
        }

        if (!response.items?.length || currentPage >= (response.totalPages || 1)) {
          hasMore = false;
        } else {
          currentPage += 1;
        }
      }

      const sinDuplicados = deduplicarMovimientosCartera(acumulados);
      const saldoInicialRow = sinDuplicados.find((m) => m.nroFormulario === 0);
      const saldoInicialFinal = saldoInicialApi ?? saldoInicialRow?.saldoAcumulado ?? 0;
      const conSaldo = aplicarSaldoAcumuladoMovimientosCartera(sinDuplicados, saldoInicialFinal);
      setTodosLosMovimientos(conSaldo);
    } catch (error) {
      console.error('Error al cargar movimientos:', error);
      enqueueSnackbar('Error al cargar los movimientos de la cartera', { variant: 'error' });
      setTodosLosMovimientos([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!carteraSeleccionada) {
      setTodosLosMovimientos([]);
      return;
    }

    const timeoutId = setTimeout(() => {
      setPage(0);
      setDrillDownFiltro(null);
      cargarMovimientosCartera(carteraSeleccionada.nroBanco);
    }, searchTerm ? 500 : 0);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carteraSeleccionada, fechaDesde, fechaHasta, searchTerm]);

  const movimientosOrdenados = useMemo(
    () => ordenarMovimientosCarteraParaVisualizacion(todosLosMovimientos),
    [todosLosMovimientos]
  );

  const tiposDocumentoEnListado = useMemo(
    () => getTiposDocumentoEnListado(todosLosMovimientos),
    [todosLosMovimientos]
  );

  const movimientosFiltradosPorTipo = useMemo(
    () => filtrarMovimientosPorTipoDocumento(movimientosOrdenados, tiposExcluidos),
    [movimientosOrdenados, tiposExcluidos]
  );

  const movimientosVisibles = useMemo(
    () => filtrarMovimientosDrillDownArqueo(movimientosFiltradosPorTipo, drillDownFiltro),
    [movimientosFiltradosPorTipo, drillDownFiltro]
  );

  const totalCount = movimientosVisibles.length;

  const movimientosPagina = useMemo(() => {
    const inicio = page * rowsPerPage;
    return movimientosVisibles.slice(inicio, inicio + rowsPerPage);
  }, [movimientosVisibles, page, rowsPerPage]);

  const filasConSaldo = useMemo<MovimientoRow[]>(() => {
    if (!carteraSeleccionada || movimientosPagina.length === 0) {
      return [];
    }

    return movimientosPagina.map((movimiento) => ({
      movimiento,
      saldoAcumulado: movimiento.saldoAcumulado ?? 0,
    }));
  }, [movimientosPagina, carteraSeleccionada]);

  // Cargar lista de carteras
  useEffect(() => {
    const cargarCarteras = async () => {
      try {
        setLoadingCarteras(true);
        // Cargar todas las carteras usando paginación
        const todasLasCarteras: SaldoCartera[] = [];
        let currentPage = 1;
        let hasMore = true;
        
        while (hasMore) {
          const response = await carteraService.getSaldos({
            pageNumber: currentPage,
            pageSize: MAX_PAGE_SIZE,
          });
          
          if (response.items && response.items.length > 0) {
            todasLasCarteras.push(...response.items);
            if (currentPage >= response.totalPages) {
              hasMore = false;
            } else {
              currentPage++;
            }
          } else {
            hasMore = false;
          }
        }
        
        const response = { items: todasLasCarteras };
        setCarteras(response.items || []);
        
        // Si hay un parámetro nroBanco en la URL, seleccionar esa cartera automáticamente
        const nroBancoParam = searchParams.get('nroBanco');
        if (nroBancoParam) {
          const nroBanco = parseInt(nroBancoParam, 10);
          const carteraEncontrada = response.items?.find(c => c.nroBanco === nroBanco);
          if (carteraEncontrada) {
            setCarteraSeleccionada(carteraEncontrada);
          }
        } else {
          const nroBancoGuardado = loadStored().nroBancoCartera;
          if (typeof nroBancoGuardado === 'number' && nroBancoGuardado > 0) {
            const carteraEncontrada = response.items?.find(c => c.nroBanco === nroBancoGuardado);
            if (carteraEncontrada) {
              setCarteraSeleccionada(carteraEncontrada);
            }
          }
        }
      } catch (error: any) {
        console.error('Error al cargar carteras:', error);
        enqueueSnackbar('Error al cargar las carteras', { variant: 'error' });
      } finally {
        setLoadingCarteras(false);
      }
    };
    cargarCarteras();
  }, [enqueueSnackbar, searchParams]);

  // Cargar movimientos cuando se selecciona una cartera
  const handleRefreshMovimientos = () => {
    if (carteraSeleccionada) {
      cargarMovimientosCartera(carteraSeleccionada.nroBanco);
    }
  };

  const handleDrillDownArqueo = (filtro: ArqueoCarteraDrillDown) => {
    setDrillDownFiltro(filtro);
    setPage(0);
    setOpenArqueoDialog(false);
    enqueueSnackbar(`Filtro aplicado: ${formatEtiquetaDrillDownArqueo(filtro)}`, { variant: 'info' });
  };

  const handleClearDrillDown = () => {
    setDrillDownFiltro(null);
    setPage(0);
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    // Asegurar que no exceda el máximo permitido
    const validRowsPerPage = Math.min(newRowsPerPage, MAX_PAGE_SIZE);
    setRowsPerPage(validRowsPerPage);
    setPage(0);
  };

  const toggleTipoDocumento = (tipoKey: string) => {
    setTiposExcluidos((prev) =>
      prev.includes(tipoKey) ? prev.filter((t) => t !== tipoKey) : [...prev, tipoKey]
    );
    setPage(0);
  };

  const getPdfDocumentType = (nroTipoFormulario?: string) => {
    const tipo = (nroTipoFormulario ?? '').trim();
    if (tipo === '4') return 'recibo' as const;
    if (tipo === '6') return 'ordenPago' as const;
    if (tipo === '17') return 'transferenciaCaja' as const;
    return undefined;
  };

  const handleConsultarMovimiento = async (movimiento: MovimientoCartera) => {
    if (movimiento.nroFormulario === 0) return;

    const pdfConfig = getMovimientoCajaPdfConfig(movimiento.nroFormulario, movimiento.nroTipoFormulario);
    try {
      setConsultandoMovimientoId(movimiento.nroFormulario);
      if (!hasBrowserSession()) {
        enqueueSnackbar('No hay token de autenticación', { variant: 'error' });
        return;
      }
      const response = await api.get(pdfConfig.url, { responseType: 'blob' });
      const cd = (response.headers as { 'content-disposition'?: string })?.['content-disposition'];
      setPdfMovimientoFileName(getFileNameFromContentDisposition(cd) ?? pdfConfig.defaultFileName);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      setPdfMovimientoUrl(url);
      setPdfMovimientoId(movimiento.nroFormulario);
      setPdfMovimientoTitle(pdfConfig.title);
      setPdfMovimientoDocumentType(getPdfDocumentType(movimiento.nroTipoFormulario));
    } catch (error: unknown) {
      const err = error as { response?: { status?: number; data?: Blob } };
      const status = err.response?.status;
      let message = 'Error al consultar el movimiento';
      if (status === 404) {
        message = 'Movimiento no encontrado.';
      } else if (status === 500 && err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const json = text ? JSON.parse(text) : null;
          if (json?.detail) message = json.detail;
        } catch {
          /* usar mensaje por defecto */
        }
      }
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setConsultandoMovimientoId(null);
    }
  };

  const handleClosePdfMovimiento = () => {
    if (pdfMovimientoUrl) window.URL.revokeObjectURL(pdfMovimientoUrl);
    setPdfMovimientoUrl(null);
    setPdfMovimientoId(null);
    setPdfMovimientoFileName(null);
    setPdfMovimientoTitle(null);
    setPdfMovimientoDocumentType(undefined);
  };

  const renderBotonConsultarMovimiento = (movimiento: MovimientoCartera, size: 'small' | 'medium' = 'small') => {
    if (movimiento.nroFormulario === 0) return null;

    const consultando = consultandoMovimientoId === movimiento.nroFormulario;
    return (
      <Tooltip title="Consultar">
        <span>
          <IconButton
            size={size}
            onClick={() => void handleConsultarMovimiento(movimiento)}
            disabled={consultando}
            sx={{
              bgcolor: '#1976d2',
              color: '#fff',
              borderRadius: 1.5,
              '&:hover': { bgcolor: '#1565c0' },
              '&.Mui-disabled': { bgcolor: '#90caf9', color: '#fff' },
            }}
            aria-label="Consultar movimiento"
          >
            {consultando ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              <VisibilityOutlinedIcon sx={{ fontSize: size === 'small' ? 18 : 20 }} />
            )}
          </IconButton>
        </span>
      </Tooltip>
    );
  };

  // Calcular totales desde los movimientos visibles por tipo de documento
  const totalIngresos = useMemo(() => {
    return movimientosFiltradosPorTipo
      .filter((mov) => mov.nroFormulario !== 0)
      .reduce((sum, mov) => sum + (mov.debe || 0), 0);
  }, [movimientosFiltradosPorTipo]);

  const totalEgresos = useMemo(() => {
    return movimientosFiltradosPorTipo
      .filter((mov) => mov.nroFormulario !== 0)
      .reduce((sum, mov) => sum + (mov.haber || 0), 0);
  }, [movimientosFiltradosPorTipo]);

  const saldoNeto = carteraSeleccionada?.saldo || 0;
  const saldoNetoPeriodo = totalIngresos - totalEgresos;

  const handleExportExcel = async () => {
    if (!carteraSeleccionada) {
      enqueueSnackbar('Debe seleccionar una cartera', { variant: 'warning' });
      return;
    }

    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
      return;
    }

    try {
      setExportingExcel(true);

      if (movimientosVisibles.length === 0) {
        enqueueSnackbar('No hay movimientos para exportar', { variant: 'warning' });
        return;
      }

      const datosExportar = movimientosVisibles.map((movimiento) => ({
        'Nro. Formulario': movimiento.nroFormulario,
        'Fecha': formatDate(movimiento.fechaEmision),
        'Tipo Formulario': getTipoMovimientoLabel(movimiento),
        'Comprobante': getComprobanteMovimientoLabel(movimiento),
        'Nombre': movimiento.nombre?.trim() || '',
        'MCP': movimiento.mcp?.trim() || '',
        'Tipo Pago': movimiento.tipoPago?.trim() || '',
        'Detalle': movimiento.detalle?.trim() || '',
        'Usuario': movimiento.usuario || '',
        'Debe': movimiento.debe || 0,
        'Haber': movimiento.haber || 0,
        'Saldo': movimiento.saldoAcumulado ?? 0,
      }));

      const worksheet = XLSX.utils.json_to_sheet(datosExportar);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Movimientos');

      const today = new Date().toISOString().slice(0, 10);
      const fileName = `movimientos_cartera_${carteraSeleccionada.nroBanco}_${today}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      enqueueSnackbar(`Datos exportados exitosamente (${movimientosVisibles.length} movimientos, ${datosExportar.length} registros)`, { variant: 'success' });
    } catch (error) {
      console.error('Error al exportar a Excel:', error);
      enqueueSnackbar('Error al exportar a Excel', { variant: 'error' });
    } finally {
      setExportingExcel(false);
    }
  };

  const handleExportPDF = async () => {
    if (!carteraSeleccionada) {
      enqueueSnackbar('Debe seleccionar una cartera', { variant: 'warning' });
      return;
    }

    if (totalCount === 0) {
      enqueueSnackbar('No hay datos para exportar', { variant: 'warning' });
      return;
    }

    try {
      setExportingPDF(true);

      if (movimientosVisibles.length === 0) {
        enqueueSnackbar('No hay movimientos para exportar', { variant: 'warning' });
        return;
      }

      const { jsPDF } = await import('jspdf');
      const autoTable = (await import('jspdf-autotable')).default;

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const marginX = 40;
      const marginY = 50;
      let currentY = marginY;

      // Título
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Movimientos de Carteras', marginX, currentY);
      currentY += 24;

      // Información de la cartera
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Cartera: ${carteraSeleccionada.nroBanco} - ${carteraSeleccionada.nombreBanco?.trim() || ''}`, marginX, currentY);
      currentY += 16;
      doc.text(`Total Ingresos del periodo: ${formatCurrency(totalIngresos)}`, marginX, currentY);
      currentY += 16;
      doc.text(`Total Egresos del periodo: ${formatCurrency(totalEgresos)}`, marginX, currentY);
      currentY += 16;
      doc.text(`Saldo Actual: ${formatCurrency(saldoNeto)}`, marginX, currentY);
      currentY += 24;

      const tableData = movimientosVisibles.map((movimiento) => {
        const debe = movimiento.debe || 0;
        const haber = movimiento.haber || 0;
        const saldo = movimiento.saldoAcumulado ?? 0;

        return [
          movimiento.nroFormulario.toString(),
          formatDate(movimiento.fechaEmision),
          getTipoMovimientoLabel(movimiento),
          getComprobanteMovimientoLabel(movimiento),
          movimiento.nombre?.trim() || '',
          movimiento.mcp?.trim() || '',
          movimiento.tipoPago?.trim() || '',
          movimiento.detalle?.trim() || '',
          debe > 0 ? formatCurrency(debe) : '-',
          haber > 0 ? formatCurrency(haber) : '-',
          formatCurrency(saldo),
          movimiento.usuario || '',
        ];
      });

      autoTable(doc, {
        startY: currentY,
        head: [['Nro. Formulario', 'Fecha', 'Tipo', 'Comprobante', 'Nombre', 'MCP', 'Tipo Pago', 'Detalle', 'Debe', 'Haber', 'Saldo', 'Usuario']],
        body: tableData,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 6, cellPadding: 2, overflow: 'linebreak' },
        headStyles: { fillColor: [25, 118, 210], textColor: 255, fontSize: 7 },
        columnStyles: {
          0: { cellWidth: 50 },
          1: { cellWidth: 60 },
          2: { cellWidth: 80 },
          3: { cellWidth: 100 },
          4: { cellWidth: 100 },
          5: { cellWidth: 100 },
          6: { cellWidth: 50 },
          7: { cellWidth: 100 },
          8: { halign: 'right', cellWidth: 70 },
          9: { halign: 'right', cellWidth: 70 },
          10: { halign: 'right', cellWidth: 70 },
          11: { cellWidth: 50 },
        },
        tableWidth: 'auto',
      });

      const pdfBlob = doc.output('blob');
      const today = new Date().toISOString().slice(0, 10);
      const fileName = `movimientos_cartera_${carteraSeleccionada.nroBanco}_${today}.pdf`;

      const url = URL.createObjectURL(pdfBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.target = '_self';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      enqueueSnackbar(`PDF descargado correctamente (${movimientosVisibles.length} movimientos, ${tableData.length} registros)`, { variant: 'success' });
    } catch (error) {
      console.error('Error al generar el PDF:', error);
      enqueueSnackbar('Error al generar el PDF', { variant: 'error' });
    } finally {
      setExportingPDF(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh', maxWidth: '100%', overflowX: 'hidden' }}>
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
            alignItems={{ xs: 'stretch', sm: 'center' }}
            spacing={2}
            sx={{ mb: 3 }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1.5}
              alignItems={{ xs: 'flex-start', sm: 'center' }}
              sx={{ flex: 1, minWidth: 0 }}
              flexWrap="wrap"
              useFlexGap
            >
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate('/tesoreria/informes')}
                sx={{ textTransform: 'none' }}
              >
                Informes
              </Button>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 600,
                  color: '#1976d2',
                  wordBreak: 'break-word',
                }}
              >
                Movimientos de Carteras
              </Typography>
            </Stack>
            {carteraSeleccionada && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  justifyContent: { xs: 'stretch', sm: 'flex-end' },
                  flexShrink: 0,
                }}
              >
                <Button
                  variant="outlined"
                  startIcon={<AssessmentIcon />}
                  onClick={() => setOpenArqueoDialog(true)}
                  disabled={loading || totalCount === 0}
                  fullWidth={isMobile}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    borderRadius: 2,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    textTransform: 'none',
                  }}
                >
                  Ver resumen
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<FileDownloadIcon />}
                  onClick={handleExportExcel}
                  disabled={loading || exportingExcel || totalCount === 0}
                  fullWidth={isMobile}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    borderRadius: 2,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    textTransform: 'none',
                  }}
                >
                  {exportingExcel ? 'Exportando...' : 'Exportar Excel'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={handleExportPDF}
                  disabled={loading || exportingPDF || totalCount === 0}
                  fullWidth={isMobile}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    borderRadius: 2,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    textTransform: 'none',
                  }}
                >
                  {exportingPDF ? 'Generando...' : 'Exportar PDF'}
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={handleRefreshMovimientos}
                  disabled={loading}
                  fullWidth={isMobile}
                  size={isMobile ? 'small' : 'medium'}
                  sx={{
                    borderRadius: 2,
                    px: { xs: 2, sm: 3 },
                    py: 1,
                    textTransform: 'none',
                  }}
                >
                  Actualizar
                </Button>
              </Stack>
            )}
          </Stack>

          {/* Selector de cartera */}
          <Box sx={{ mb: 3 }}>
            <Autocomplete
              {...searchableSelectAutocompleteProps}
              options={carteras}
              getOptionLabel={(option) => `${option.nroBanco} - ${option.nombreBanco?.trim() || ''}`}
              value={carteraSeleccionada}
              onChange={(event, newValue) => {
                setCarteraSeleccionada(newValue);
              }}
              loading={loadingCarteras}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar Cartera"
                  placeholder="Buscar cartera por número o nombre..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <InputAdornment position="start">
                          <AccountBalanceIcon sx={{ color: '#666' }} />
                        </InputAdornment>
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      },
                    },
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box component="li" {...props} key={option.nroBanco}>
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%' }}>
                    <AccountBalanceIcon sx={{ color: '#1976d2' }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body1" sx={{ fontWeight: 500 }}>
                        {option.nombreBanco?.trim() || '-'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Nro: {option.nroBanco} | Saldo: {formatCurrency(option.saldo || 0)}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>
              )}
            />
          </Box>

          {/* Resumen de movimientos */}
          {carteraSeleccionada && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#4caf50', color: '#fff', textAlign: 'center', py: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(totalIngresos)}
                  </Typography>
                  <Typography variant="caption">Total Ingresos del periodo</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#f44336', color: '#fff', textAlign: 'center', py: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(totalEgresos)}
                  </Typography>
                  <Typography variant="caption">Total Egresos del periodo</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#ff9800', color: '#fff', textAlign: 'center', py: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(saldoNetoPeriodo)}
                  </Typography>
                  <Typography variant="caption">Saldo neto del periodo</Typography>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card sx={{ bgcolor: '#1976d2', color: '#fff', textAlign: 'center', py: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    {formatCurrency(saldoNeto)}
                  </Typography>
                  <Typography variant="caption">Saldo actual de la cartera</Typography>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Filtros */}
          {carteraSeleccionada && (
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Buscar"
                  placeholder="Buscar por descripción, nombre, comprobante, letra o usuario..."
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setPage(0);
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: '#666' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setSearchTerm('');
                            setPage(0);
                          }}
                          sx={{ p: 0.5 }}
                        >
                          <ClearIcon fontSize="small" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => {
                    setFechaDesde(e.target.value);
                    setPage(0);
                  }}
                  InputLabelProps={{ shrink: true }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#fafafa',
                      '&:hover': {
                        bgcolor: '#f5f5f5',
                      },
                      '&.Mui-focused': {
                        bgcolor: '#ffffff',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <TextField
                    fullWidth
                    label="Fecha Hasta"
                    type="date"
                    value={fechaHasta}
                    onChange={(e) => {
                      setFechaHasta(e.target.value);
                      setPage(0);
                    }}
                    InputLabelProps={{ shrink: true }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#fafafa',
                        '&:hover': {
                          bgcolor: '#f5f5f5',
                        },
                        '&.Mui-focused': {
                          bgcolor: '#ffffff',
                        },
                      },
                    }}
                  />
                  {(fechaDesde ||
                    fechaHasta ||
                    searchTerm ||
                    drillDownFiltro ||
                    !tiposExcluidosSonDefault(tiposExcluidos)) && (
                    <Button
                      variant="outlined"
                      startIcon={<ClearIcon />}
                      onClick={() => {
                        setFechaDesde('');
                        setFechaHasta('');
                        setSearchTerm('');
                        setDrillDownFiltro(null);
                        setTiposExcluidos([...TIPOS_DOCUMENTO_EXCLUIDOS_DEFAULT]);
                        setPage(0);
                      }}
                      fullWidth={isMobile}
                      sx={{
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        textTransform: 'none',
                        minWidth: 'auto',
                      }}
                    >
                      Limpiar
                    </Button>
                  )}
                </Stack>
              </Grid>
            </Grid>
          )}

          {carteraSeleccionada && tiposDocumentoEnListado.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                  Tipo de documento
                </Typography>
                <Stack direction="row" flexWrap="wrap" useFlexGap gap={1} alignItems="center">
                  {tiposDocumentoEnListado.map(({ key, label }) => {
                    const incluido = !tiposExcluidos.includes(key);
                    return (
                      <Chip
                        key={key}
                        label={label}
                        size="small"
                        onClick={() => toggleTipoDocumento(key)}
                        color={incluido ? 'primary' : 'default'}
                        variant={incluido ? 'filled' : 'outlined'}
                        sx={{ borderRadius: 2 }}
                      />
                    );
                  })}
                </Stack>
              </Stack>
            </Box>
          )}

          {carteraSeleccionada && drillDownFiltro && (
            <Box sx={{ mb: 2 }}>
              <Chip
                label={`Filtro resumen: ${formatEtiquetaDrillDownArqueo(drillDownFiltro)}`}
                onDelete={handleClearDrillDown}
                color="primary"
                variant="outlined"
                sx={{ borderRadius: 2 }}
              />
            </Box>
          )}

          {/* Tabla de movimientos */}
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : carteraSeleccionada ? (
            filasConSaldo.length > 0 ? (
              <>
                {isMobile ? (
                  <Stack spacing={2}>
                    {filasConSaldo.map((fila, idx) => (
                      <Card
                        key={`${fila.movimiento.nroFormulario}-${idx}`}
                        sx={{
                          borderRadius: 2,
                          border: '1px solid #e0e0e0',
                          '&:hover': {
                            boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                          },
                        }}
                      >
                        <CardContent>
                          <Stack spacing={1.5}>
                            <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                              <Box sx={{ minWidth: 0, flex: 1 }}>
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                  Formulario #{fila.movimiento.nroFormulario}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {formatDate(fila.movimiento.fechaEmision)} | Tipo: {getTipoMovimientoLabel(fila.movimiento)}
                                </Typography>
                              </Box>
                              {renderBotonConsultarMovimiento(fila.movimiento)}
                            </Stack>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Comprobante:</strong> {getComprobanteMovimientoLabel(fila.movimiento)}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              <strong>Nombre:</strong> {fila.movimiento.nombre?.trim() || '-'}
                            </Typography>
                            {fila.movimiento.descripcion && (
                              <Typography variant="body2" color="text.secondary">
                                <strong>Descripción:</strong> {fila.movimiento.descripcion.trim()}
                              </Typography>
                            )}
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f9f9f9', borderRadius: 1 }}>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                                Medio de Pago:
                              </Typography>
                              <Stack spacing={0.5}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {fila.movimiento.mcp?.trim() || '-'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  Tipo: {fila.movimiento.tipoPago?.trim() || '-'}
                                </Typography>
                                {fila.movimiento.detalle?.trim() && (
                                  <Typography variant="caption" color="text.secondary">
                                    {fila.movimiento.detalle.trim()}
                                  </Typography>
                                )}
                              </Stack>
                            </Box>
                            <Box sx={{ mt: 1, p: 1.5, bgcolor: '#f0f0f0', borderRadius: 1 }}>
                              <Stack spacing={0.5}>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Debe:</strong> {fila.movimiento.debe ? formatCurrency(fila.movimiento.debe) : '-'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  <strong>Haber:</strong> {fila.movimiento.haber ? formatCurrency(fila.movimiento.haber) : '-'}
                                </Typography>
                                <Typography 
                                  variant="body2" 
                                  sx={{ 
                                    fontWeight: 600,
                                    color: (fila.saldoAcumulado || 0) >= 0 ? '#4caf50' : '#f44336',
                                  }}
                                >
                                  <strong>Saldo:</strong> {formatCurrency(fila.saldoAcumulado || 0)}
                                </Typography>
                              </Stack>
                            </Box>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              <strong>Usuario:</strong> {fila.movimiento.usuario || '-'}
                            </Typography>
                          </Stack>
                        </CardContent>
                      </Card>
                    ))}
                    <TablePagination
                      component="div"
                      count={totalCount}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[10, 20]}
                      labelRowsPerPage="Filas por página"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                    />
                  </Stack>
                ) : (
                  <TableContainer
                    component={Paper}
                    sx={{
                      borderRadius: 2,
                      boxShadow: 'none',
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <Table
                      size="small"
                      sx={{
                        '& .MuiTableCell-root': {
                          py: 0.75,
                          px: 1.25,
                          fontSize: '0.8125rem',
                          lineHeight: 1.2,
                        },
                        '& .MuiTableCell-head': {
                          py: 0.75,
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                          <TableCell sx={{ fontWeight: 600 }}>Nro. Formulario</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Fecha</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Tipo</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Comprobante</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Nombre</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>MCP</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Tipo Pago</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Debe</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Haber</TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>Saldo</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Usuario</TableCell>
                          <TableCell align="center" sx={{ fontWeight: 600, width: 56 }}>Consultar</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filasConSaldo.map((fila, idx) => {
                          const esSaldoInicial = fila.movimiento.nroFormulario === 0;
                          const esIngreso = (fila.movimiento.debe || 0) > 0;
                          const detalle = fila.movimiento.detalle?.trim();
                          const rowKey = `${fila.movimiento.nroFormulario}-${idx}`;
                          return (
                            <React.Fragment key={rowKey}>
                              <TableRow
                                sx={{
                                  '&:hover': { bgcolor: '#fafafa' },
                                  ...(detalle ? { '& > td': { borderBottom: 'none' } } : {}),
                                }}
                              >
                                <TableCell sx={{ fontWeight: 500 }}>
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    {!esSaldoInicial && (esIngreso ? (
                                      <ArrowUpwardIcon sx={{ fontSize: 14, color: '#4caf50' }} />
                                    ) : (
                                      <ArrowDownwardIcon sx={{ fontSize: 14, color: '#f44336' }} />
                                    ))}
                                    <span>{fila.movimiento.nroFormulario}</span>
                                  </Stack>
                                </TableCell>
                                <TableCell>{formatDate(fila.movimiento.fechaEmision)}</TableCell>
                                <TableCell>
                                  <Chip
                                    label={getTipoMovimientoLabel(fila.movimiento)}
                                    size="small"
                                    sx={{
                                      height: 20,
                                      fontSize: '0.585rem',
                                      bgcolor: esSaldoInicial ? '#1976d2' : esIngreso ? '#4caf50' : '#f44336',
                                      color: '#fff',
                                      '& .MuiChip-label': { px: 0.675 },
                                    }}
                                  />
                                </TableCell>
                                <TableCell>
                                  {getComprobanteMovimientoLabel(fila.movimiento)}
                                </TableCell>
                                <TableCell sx={{ fontWeight: 600 }}>{fila.movimiento.nombre?.trim() || '-'}</TableCell>
                                <TableCell>{fila.movimiento.mcp?.trim() || '-'}</TableCell>
                                <TableCell>{fila.movimiento.tipoPago?.trim() || '-'}</TableCell>
                                <TableCell align="right">
                                  {fila.movimiento.debe ? formatCurrency(fila.movimiento.debe) : '-'}
                                </TableCell>
                                <TableCell align="right">
                                  {fila.movimiento.haber ? formatCurrency(fila.movimiento.haber) : '-'}
                                </TableCell>
                                <TableCell
                                  align="right"
                                  sx={{
                                    fontWeight: 600,
                                    color: (fila.saldoAcumulado || 0) >= 0 ? '#4caf50' : '#f44336',
                                  }}
                                >
                                  {formatCurrency(fila.saldoAcumulado || 0)}
                                </TableCell>
                                <TableCell>{fila.movimiento.usuario || '-'}</TableCell>
                                <TableCell align="center">
                                  {renderBotonConsultarMovimiento(fila.movimiento)}
                                </TableCell>
                              </TableRow>
                              {detalle && (
                                <TableRow sx={{ '&:hover': { bgcolor: '#fafafa' } }}>
                                  <TableCell
                                    colSpan={12}
                                    sx={{
                                      py: 0.25,
                                      pt: 0,
                                      pb: 0.5,
                                      color: 'text.secondary',
                                      fontSize: '0.7rem',
                                      lineHeight: 1.2,
                                    }}
                                  >
                                    {detalle}
                                  </TableCell>
                                </TableRow>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </TableBody>
                    </Table>
                    <TablePagination
                      component="div"
                      count={totalCount}
                      page={page}
                      onPageChange={handleChangePage}
                      rowsPerPage={rowsPerPage}
                      onRowsPerPageChange={handleChangeRowsPerPage}
                      rowsPerPageOptions={[10, 20]}
                      labelRowsPerPage="Filas por página"
                      labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count !== -1 ? count : `más de ${to}`}`}
                      sx={{
                        borderTop: '1px solid #e0e0e0',
                      }}
                    />
                  </TableContainer>
                )}
              </>
            ) : (
              <Box sx={{ textAlign: 'center', py: 4 }}>
                <Typography color="text.secondary">
                  No se encontraron movimientos para esta cartera
                </Typography>
              </Box>
            )
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <AccountBalanceIcon sx={{ fontSize: 64, color: '#ccc', mb: 2 }} />
              <Typography color="text.secondary">
                Selecciona una cartera para ver sus movimientos
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {carteraSeleccionada && (
        <ArqueoCarteraDialog
          open={openArqueoDialog}
          onClose={() => setOpenArqueoDialog(false)}
          onDrillDown={handleDrillDownArqueo}
          movimientos={todosLosMovimientos}
          carteraLabel={`${carteraSeleccionada.nroBanco} - ${carteraSeleccionada.nombreBanco?.trim() || ''}`}
          fechaDesde={fechaDesde}
          fechaHasta={fechaHasta}
          saldoActual={carteraSeleccionada.saldo}
        />
      )}

      {pdfMovimientoUrl && pdfMovimientoId != null && (
        <PDFViewer
          pdfUrl={pdfMovimientoUrl}
          onClose={handleClosePdfMovimiento}
          title={pdfMovimientoTitle ?? `Movimiento ${pdfMovimientoId}`}
          documentId={`${pdfMovimientoId}`}
          documentType={pdfMovimientoDocumentType}
          downloadFileName={pdfMovimientoFileName ?? `movimiento_${pdfMovimientoId}.pdf`}
        />
      )}
    </Box>
  );
};

export default MovimientosCarteras;
